/**
 * PlotSizingCalculator.ts
 * Class for calculating optimal test plot sizes and buffer widths based on field boundary
 * and equipment specifications
 */

import * as turf from '@turf/turf';

export type EquipmentUnits = 'ft' | 'm' | 'cm' | 'in';

export interface PlotSizingCalculatorConfig {
  executionEquipmentSwathWidth: number;
  executionEquipmentSwathWidthUnits: EquipmentUnits;
  executionEquipmentRateChangeDistance: number;
  executionEquipmentRateChangeDistanceUnits: EquipmentUnits;
  executionEquipmentCanSplitSwath: boolean;
  executionEquipmentDgpsAccuracy: number;
  executionEquipmentDgpsAccuracyUnits: EquipmentUnits;
  harvesterSwathWidth: number;
  harvesterSwathWidthUnits: EquipmentUnits;
  harvesterSpatialResolution: number;
  harvesterSpatialResolutionUnits: EquipmentUnits;
  harvesterDgpsAccuracy: number;
  harvesterDgpsAccuracyUnits: EquipmentUnits;
  treatmentDrift: number;
  treatmentDriftUnits: EquipmentUnits;
  maxLikelyHarvestAngle: number;
}

export interface PlotSizingResults {
  suggestedPlotLengthFt: number;
  suggestedPlotWidthFt: number;
  suggestedPlotBufferWidthFt: number;
  suggestedPlotBufferLengthFt: number;
  suggestedFieldBufferWidthFt: number;
  plotsPerAcre: number;
  effectiveExecutionWidthFt: number;
}

export class PlotSizingCalculator {
  // Execution equipment properties (all stored in feet)
  public readonly executionEquipmentSwathWidthFt: number;
  public readonly executionEquipmentRateChangeDistanceFt: number;
  public readonly executionEquipmentCanSplitSwath: boolean;
  public readonly executionEquipmentDgpsAccuracy: number;
  public readonly executionEquipmentDgpsAccuracyUnits: EquipmentUnits;

  // Harvester properties (all stored in feet)
  public readonly harvesterSwathWidthFt: number;
  public readonly harvesterSpatialResolutionFt: number;
  public readonly harvesterDgpsAccuracy: number;
  public readonly harvesterDgpsAccuracyUnits: EquipmentUnits;

  // Treatment drift properties (all stored in feet)
  public readonly treatmentDriftFt: number;

  // Other properties
  public readonly maxLikelyHarvestAngle: number;

  // Calculated complicating factors
  private gpsErrorDistanceFt: number = 0;
  private equipmentWidthMismatchFt: number = 0;
  private effectiveExecutionWidthFt: number = 0; // The execution width we'll actually use (full or half if splittable)
  private suggestedPlotWidthFt: number = 0;
  private suggestedPlotLengthFt: number = 0;
  private suggestedPlotBufferLengthFt: number = 0;
  private suggestedPlotBufferWidthFt: number = 0;
  private suggestedFieldBufferWidthFt: number = 0;

  // Conversion factor from meters to feet (1 foot = 0.3048 meter, see https://www.nist.gov/pml/us-surveyfoot)
  private static readonly METERS_TO_FEET_DIVISOR = 0.3048;

  // Conversion factor for acres to square feet
  private static readonly SQUARE_FEET_PER_ACRE = 43560;

  constructor(config: PlotSizingCalculatorConfig) {
    // Convert and store execution equipment measurements
    this.executionEquipmentSwathWidthFt = this.convertToFeet(
      config.executionEquipmentSwathWidth,
      config.executionEquipmentSwathWidthUnits,
    );
    this.executionEquipmentRateChangeDistanceFt = this.convertToFeet(
      config.executionEquipmentRateChangeDistance,
      config.executionEquipmentRateChangeDistanceUnits,
    );
    this.executionEquipmentCanSplitSwath =
      config.executionEquipmentCanSplitSwath;
    this.executionEquipmentDgpsAccuracy = config.executionEquipmentDgpsAccuracy;
    this.executionEquipmentDgpsAccuracyUnits =
      config.executionEquipmentDgpsAccuracyUnits;

    // Convert and store harvester measurements
    this.harvesterSwathWidthFt = this.convertToFeet(
      config.harvesterSwathWidth,
      config.harvesterSwathWidthUnits,
    );
    this.harvesterSpatialResolutionFt = this.convertToFeet(
      config.harvesterSpatialResolution,
      config.harvesterSpatialResolutionUnits,
    );
    this.harvesterDgpsAccuracy = config.harvesterDgpsAccuracy;
    this.harvesterDgpsAccuracyUnits = config.harvesterDgpsAccuracyUnits;

    // Convert and store treatment drift measurements
    this.treatmentDriftFt = this.convertToFeet(
      config.treatmentDrift,
      config.treatmentDriftUnits,
    );

    // Store other properties
    this.maxLikelyHarvestAngle = config.maxLikelyHarvestAngle;
  }

  /**
   * Converts a measurement to feet from various units
   */
  private convertToFeet(value: number, units: EquipmentUnits): number {
    switch (units) {
      case 'm':
        return value / PlotSizingCalculator.METERS_TO_FEET_DIVISOR;
      case 'cm':
        return value / (PlotSizingCalculator.METERS_TO_FEET_DIVISOR * 100); // 100 cm = 1 m
      case 'in':
        return value / 12; // 12 inches = 1 foot
      case 'ft':
      default:
        return value;
    }
  }

  /**
   * Calculates GPS error distance based on GPS uncertainty values from both machines
   */
  private calculateGpsErrorDistance(): void {
    // Convert uncertainties to feet
    const executionUncertaintyFt = this.convertToFeet(
      this.executionEquipmentDgpsAccuracy,
      this.executionEquipmentDgpsAccuracyUnits,
    );
    const harvesterUncertaintyFt = this.convertToFeet(
      this.harvesterDgpsAccuracy,
      this.harvesterDgpsAccuracyUnits,
    );

    // Calculate total GPS uncertainty using RSS (root sum of squares)
    const gpsUncertaintyTotal = Math.sqrt(
      Math.pow(executionUncertaintyFt, 2) + Math.pow(harvesterUncertaintyFt, 2),
    );

    // Define tolerance: 10.5 inches = 45% of 24 inches
    const gpsUncertaintyToleranceFt = 10.5 / 12; // Convert inches to feet

    // If total is below tolerance, ignore it; otherwise use it as error distance
    this.gpsErrorDistanceFt =
      gpsUncertaintyTotal < gpsUncertaintyToleranceFt ? 0 : gpsUncertaintyTotal;
  }

  /**
   * Determines the best effective execution width (full or half if splittable) based on harvester complementarity
   */
  private calculateEffectiveExecutionWidth(): void {
    const fullWidth = this.executionEquipmentSwathWidthFt;
    const halfWidth = fullWidth / 2;

    // Always consider full width
    const fullWidthComplementarity = this.getComplementarityScore(fullWidth);

    // If equipment can split swath, also consider half width
    if (this.executionEquipmentCanSplitSwath) {
      const halfWidthComplementarity = this.getComplementarityScore(halfWidth);

      // Use half width if it provides better complementarity, or equal complementarity (prefer smaller width)
      this.effectiveExecutionWidthFt =
        halfWidthComplementarity >= fullWidthComplementarity
          ? halfWidth
          : fullWidth;
    } else {
      this.effectiveExecutionWidthFt = fullWidth;
    }
  }

  /**
   * Gets a complementarity score for a given execution width (higher = better complementarity)
   */
  private getComplementarityScore(executionWidth: number): number {
    const ratio = executionWidth / this.harvesterSwathWidthFt;

    // Exact multiple is best (score = 3)
    if (Number.isInteger(ratio)) {
      return 3;
    }

    // Half-integer multiple is good (score = 2)
    if (Number.isInteger(ratio - 0.5)) {
      return 2;
    }

    // No complementarity (score = 1)
    return 1;
  }

  /**
   * Checks if effective execution width is an exact multiple of harvester width
   */
  private isExactMultiple(): boolean {
    const ratio = this.effectiveExecutionWidthFt / this.harvesterSwathWidthFt;
    return Number.isInteger(ratio);
  }

  /**
   * Checks if effective execution width is an exact half-integer multiple of harvester width
   */
  private isExactHalfIntegerMultiple(): boolean {
    const ratio = this.effectiveExecutionWidthFt / this.harvesterSwathWidthFt;
    return Number.isInteger(ratio - 0.5);
  }

  /**
   * Determines if we're harvesting at an angle (anything > 0 degrees)
   */
  private isHarvestingAtAngle(): boolean {
    return this.maxLikelyHarvestAngle > 0;
  }

  /**
   * Calculates the amount of space that needs to be added to the plot width to account for the equipment width mismatch
   */
  private calculateEquipmentWidthMismatch(): void {
    // Check equipment complementarity using effective width
    const harvestingAtAngle = this.isHarvestingAtAngle();

    if (this.effectiveExecutionWidthFt >= this.harvesterSwathWidthFt) {
      if (this.isExactMultiple() && !harvestingAtAngle) {
        this.equipmentWidthMismatchFt = 0;
      } else if (this.isExactHalfIntegerMultiple() && !harvestingAtAngle) {
        // Calculate minimum execution passes needed to get >= 2 harvester passes
        const nExecutionPasses = Math.ceil(
          (2 * this.harvesterSwathWidthFt) / this.effectiveExecutionWidthFt,
        );
        this.equipmentWidthMismatchFt =
          nExecutionPasses % 2 === 0 ? 0 : 0.5 * this.harvesterSwathWidthFt;
      } else {
        // "else" case - includes harvesting at angle
        // For cases where execution width >> harvester width, use a smarter penalty
        const widthRatio =
          this.effectiveExecutionWidthFt / this.harvesterSwathWidthFt;

        if (widthRatio >= 2.0) {
          // When execution width is 2x+ larger than harvester, there's plenty of room
          // No mismatch penalty needed - the execution pass covers multiple harvester passes easily
          this.equipmentWidthMismatchFt = 0;
        } else if (widthRatio > 1.0) {
          // For moderate size differences, use a small penalty based on remainder
          const remainder =
            this.effectiveExecutionWidthFt % this.harvesterSwathWidthFt;
          this.equipmentWidthMismatchFt = Math.min(
            remainder * 0.5,
            this.harvesterSwathWidthFt * 0.25,
          );
        } else {
          // For closer width relationships, use the full harvester width penalty
          this.equipmentWidthMismatchFt = this.harvesterSwathWidthFt;
        }
      }
    } else {
      // effective execution width < harvester width
      if (
        (this.isExactMultiple() || this.isExactHalfIntegerMultiple()) &&
        !harvestingAtAngle
      ) {
        this.equipmentWidthMismatchFt = 0;
      } else {
        this.equipmentWidthMismatchFt = this.harvesterSwathWidthFt;
      }
    }
  }

  /**
   * Calculates the suggested plot width based on equipment constraints and GPS requirements
   */
  private calculatePlotWidth(): void {
    // Determine minimum number of harvester passes needed (simplified to always use 2)
    const minHarvesterPasses = 2;
    const minWidthFromHarvester =
      minHarvesterPasses * this.harvesterSwathWidthFt;

    // Calculate minimum execution passes needed using the effective execution width
    const minExecutionPasses = Math.ceil(
      minWidthFromHarvester / this.effectiveExecutionWidthFt,
    );
    let plotWidth = minExecutionPasses * this.effectiveExecutionWidthFt;

    // Add equipment width mismatch (accounts for GPS and equipment complementarity)
    plotWidth += this.equipmentWidthMismatchFt;

    // Add GPS error margin (accounts for GPS precision during execution vs harvest)
    plotWidth += this.gpsErrorDistanceFt;

    // Ensure final width is still a multiple of effective execution width
    const finalExecutionPasses = Math.ceil(
      plotWidth / this.effectiveExecutionWidthFt,
    );
    this.suggestedPlotWidthFt =
      finalExecutionPasses * this.effectiveExecutionWidthFt;
  }

  /**
   * Calculates the suggested plot length and length buffer based on desired good data length and buffer requirements
   */
  private calculatePlotLength(): void {
    // Determine desired length of good data in the middle (excluding buffers)
    const hasRateChangeAndResolution =
      this.executionEquipmentRateChangeDistanceFt > 0 &&
      this.harvesterSpatialResolutionFt > 0;
    const goodDataLengthFt = hasRateChangeAndResolution ? 150 : 200;

    // Calculate buffer length based on rate change distance and spatial resolution
    let bufferLengthFt = 0;

    if (hasRateChangeAndResolution) {
      // "buffer is the larger of those two values" - but also consider drift length
      bufferLengthFt = Math.max(
        this.executionEquipmentRateChangeDistanceFt,
        this.harvesterSpatialResolutionFt,
        this.treatmentDriftFt,
      );
    } else {
      // Default buffer: 25 feet (simplified since we always have GPS accuracy)
      bufferLengthFt = Math.max(25, this.treatmentDriftFt);
    }

    // Store buffer length and calculate total length
    this.suggestedPlotBufferLengthFt = bufferLengthFt;
    this.suggestedPlotLengthFt = goodDataLengthFt + 2 * bufferLengthFt;
  }

  /**
   * Calculates the suggested plot width buffer based on drift, angle compensation, and equipment factors
   */
  private calculatePlotBufferWidth(): void {
    const harvestingAtAngle = this.isHarvestingAtAngle();

    // Start with drift width requirement
    let bufferWidth = this.treatmentDriftFt;

    // Add angle compensation if harvesting at an angle
    if (harvestingAtAngle) {
      const angleRadians = (this.maxLikelyHarvestAngle * Math.PI) / 180;
      bufferWidth += Math.tan(angleRadians) * this.suggestedPlotLengthFt;
    }

    // Apply equipment-based buffer requirements
    const hasGoodComplementarity =
      (this.isExactMultiple() || this.isExactHalfIntegerMultiple()) &&
      !harvestingAtAngle;

    if (this.effectiveExecutionWidthFt >= this.harvesterSwathWidthFt) {
      if (this.isExactMultiple() && !harvestingAtAngle) {
        bufferWidth = Math.max(0, this.treatmentDriftFt);
      } else if (this.isExactHalfIntegerMultiple() && !harvestingAtAngle) {
        bufferWidth = Math.max(
          0.5 * this.harvesterSwathWidthFt,
          this.treatmentDriftFt,
        );
      } else {
        bufferWidth = Math.max(this.harvesterSwathWidthFt, bufferWidth);
      }
    } else {
      // effective execution width < harvester width
      if (hasGoodComplementarity) {
        bufferWidth = Math.max(0, this.treatmentDriftFt);
      } else {
        bufferWidth = Math.max(this.harvesterSwathWidthFt, bufferWidth);
      }
    }

    this.suggestedPlotBufferWidthFt = bufferWidth;
  }

  /**
   * Calculates how many plots can fit per acre, accounting for field buffer and plot spacing
   */
  private calculatePlotsPerAcre(): number {
    // Use a large field (160 acres) to get meaningful density calculation
    const fieldSizeAcres = 160;
    const fieldAreaSqFt =
      fieldSizeAcres * PlotSizingCalculator.SQUARE_FEET_PER_ACRE;

    // Assume square field for calculation (side length = sqrt of area)
    const fieldSideLengthFt = Math.sqrt(fieldAreaSqFt);

    // Subtract field buffer from all sides to get usable area
    const usableSideLengthFt =
      fieldSideLengthFt - 2 * this.suggestedFieldBufferWidthFt;

    // If field buffer is too large even for a 160-acre field, return 0
    if (usableSideLengthFt <= 0) {
      return 0;
    }

    // Calculate total usable area
    const usableAreaSqFt = usableSideLengthFt * usableSideLengthFt;

    // Calculate area needed per plot (including plot buffers)
    const plotTotalWidthFt =
      this.suggestedPlotWidthFt + 2 * this.suggestedPlotBufferWidthFt;
    const plotTotalLengthFt =
      this.suggestedPlotLengthFt + 2 * this.suggestedPlotBufferLengthFt;
    const areaPerPlotSqFt = plotTotalWidthFt * plotTotalLengthFt;

    // Calculate number of plots that fit in the large field
    const totalPlotsInField = Math.floor(usableAreaSqFt / areaPerPlotSqFt);

    // Convert to plots per acre
    const plotsPerAcre = totalPlotsInField / fieldSizeAcres;

    return plotsPerAcre;
  }

  /**
   * Calculates the suggested field buffer width as a multiple of execution swath width targeting ~100ft
   */
  private calculateFieldBufferWidth(): void {
    const targetBufferWidth = 100;

    // Calculate the multiple of effective execution width closest to 100ft
    const baseMultiple = Math.round(
      targetBufferWidth / this.effectiveExecutionWidthFt,
    );
    const executionMultiple = Math.max(1, baseMultiple); // Ensure it's at least 1
    let fieldBufferWidth = executionMultiple * this.effectiveExecutionWidthFt;

    // Try to optimize for plot width compatibility if possible
    if (this.suggestedPlotWidthFt > 0) {
      const plotWidthToConsider =
        this.suggestedPlotWidthFt > 150
          ? this.suggestedPlotWidthFt / 2
          : this.suggestedPlotWidthFt;

      // Look for a common multiple close to our target
      for (let plotMultiple = 1; plotMultiple <= 3; plotMultiple++) {
        for (
          let execMultiple = Math.max(1, baseMultiple - 1);
          execMultiple <= baseMultiple + 1;
          execMultiple++
        ) {
          const candidate = execMultiple * this.effectiveExecutionWidthFt;

          // Check if this candidate is also a multiple of plot width
          if (Math.abs(candidate - plotMultiple * plotWidthToConsider) < 0.01) {
            // Found a common multiple, check if it's closer to target than current choice
            if (
              Math.abs(candidate - targetBufferWidth) <
              Math.abs(fieldBufferWidth - targetBufferWidth)
            ) {
              fieldBufferWidth = candidate;
            }
          }
        }
      }
    }

    this.suggestedFieldBufferWidthFt = fieldBufferWidth;
  }

  /**
   * Final checks and automatic corrections
   */
  private finalValidation(): void {
    // Apply minimum width constraint (simplified to 30ft since we always have GPS)
    const absoluteMinWidth = 30;

    // Apply minimum width constraint if needed
    if (this.suggestedPlotWidthFt < absoluteMinWidth) {
      const minExecutionPassesForGps = Math.ceil(
        absoluteMinWidth / this.effectiveExecutionWidthFt,
      );
      this.suggestedPlotWidthFt =
        minExecutionPassesForGps * this.effectiveExecutionWidthFt;
    }

    // AUTOMATIC CORRECTIONS

    // 1. Fix insufficient harvester passes
    const actualHarvesterPasses =
      this.suggestedPlotWidthFt / this.harvesterSwathWidthFt;
    const hasGoodComplementarity =
      (this.isExactMultiple() || this.isExactHalfIntegerMultiple()) &&
      !this.isHarvestingAtAngle();
    const minRequiredPasses = hasGoodComplementarity ? 2.0 : 2.1;

    if (actualHarvesterPasses < minRequiredPasses) {
      const requiredWidth = minRequiredPasses * this.harvesterSwathWidthFt;
      const requiredExecutionPasses = Math.ceil(
        requiredWidth / this.effectiveExecutionWidthFt,
      );
      this.suggestedPlotWidthFt =
        requiredExecutionPasses * this.effectiveExecutionWidthFt;
    }

    // 2. Fix insufficient usable areas
    const minUsableWidth = 0.5 * this.harvesterSwathWidthFt;
    const usableWidth =
      this.suggestedPlotWidthFt - 2 * this.suggestedPlotBufferWidthFt;

    if (usableWidth < minUsableWidth) {
      const requiredPlotWidth =
        2 * this.suggestedPlotBufferWidthFt + minUsableWidth;
      const requiredExecutionPasses = Math.ceil(
        requiredPlotWidth / this.effectiveExecutionWidthFt,
      );
      this.suggestedPlotWidthFt =
        requiredExecutionPasses * this.effectiveExecutionWidthFt;
    }

    // 3. Ensure target good data length
    const targetGoodDataLength =
      this.executionEquipmentRateChangeDistanceFt > 0 &&
      this.harvesterSpatialResolutionFt > 0
        ? 150
        : 200;
    const usableLength =
      this.suggestedPlotLengthFt - 2 * this.suggestedPlotBufferLengthFt;

    if (usableLength < targetGoodDataLength) {
      this.suggestedPlotLengthFt =
        2 * this.suggestedPlotBufferLengthFt + targetGoodDataLength;
    }
  }

  /**
   * Container method that orchestrates the plot sizing calculations
   */
  public calculatePlotSizing(): PlotSizingResults {
    // Calculate complicating factors
    this.calculateGpsErrorDistance();
    this.calculateEffectiveExecutionWidth();
    this.calculateEquipmentWidthMismatch();

    // Calculate plot dimensions
    this.calculatePlotWidth();
    this.calculatePlotLength();

    // Calculate buffers (width buffer depends on length, so calculate after length)
    this.calculatePlotBufferWidth();

    // Calculate field buffer (depends on plot width, so calculate after plot dimensions)
    this.calculateFieldBufferWidth();

    // Apply final GPS-based constraints to all dimensions
    this.finalValidation();

    // Calculate plots per acre (must be done after all other calculations)
    const plotsPerAcre = this.calculatePlotsPerAcre();

    // Return all calculated values
    return {
      suggestedPlotLengthFt: this.suggestedPlotLengthFt,
      suggestedPlotWidthFt: this.suggestedPlotWidthFt,
      suggestedPlotBufferWidthFt: this.suggestedPlotBufferWidthFt,
      suggestedPlotBufferLengthFt: this.suggestedPlotBufferLengthFt,
      suggestedFieldBufferWidthFt: this.suggestedFieldBufferWidthFt,
      plotsPerAcre: plotsPerAcre,
      effectiveExecutionWidthFt: this.effectiveExecutionWidthFt,
    };
  }

  /**
   * Estimate total plot count for a given field area and plot dimensions
   * @param fieldAreaAcres - Field area in acres
   * @param plotWidthFt - Plot width in feet
   * @param plotLengthFt - Plot length in feet
   * @param fieldBufferFt - Field buffer width in feet
   * @param plotBufferWidthFt - Plot buffer width in feet
   * @param plotBufferLengthFt - Plot buffer length in feet
   * @param isPlotType - Whether this is a plot trial (true) or strip trial (false)
   * @returns Estimated number of plots that would be created
   */
  public static estimatePlotCount(
    fieldAreaAcres: number,
    plotWidthFt: number,
    plotLengthFt: number,
    fieldBufferFt: number,
    plotBufferWidthFt: number = 0,
    plotBufferLengthFt: number = 0,
    isPlotType: boolean = true,
  ): number {
    if (fieldAreaAcres <= 0 || plotWidthFt <= 0 || plotLengthFt <= 0) {
      return 0;
    }

    // Calculate usable area after field buffer
    // Approximate field buffer impact - assume square field for simplicity
    const fieldSideLengthFt = Math.sqrt(
      fieldAreaAcres * PlotSizingCalculator.SQUARE_FEET_PER_ACRE,
    );
    const usableSideLengthFt = fieldSideLengthFt - 2 * fieldBufferFt;

    if (usableSideLengthFt <= 0) {
      return 0;
    }

    const usableAreaSqFt = usableSideLengthFt * usableSideLengthFt;

    // Calculate area needed per plot (including plot buffers)
    const plotTotalWidthFt = plotWidthFt + 2 * plotBufferWidthFt;
    const plotTotalLengthFt = plotLengthFt + 2 * plotBufferLengthFt;
    const areaPerPlotSqFt = plotTotalWidthFt * plotTotalLengthFt;

    if (areaPerPlotSqFt <= 0) {
      return 0;
    }

    // Calculate number of plots that fit
    const totalPlotsInField = Math.floor(usableAreaSqFt / areaPerPlotSqFt);

    // Apply packing efficiency - plots don't arrange perfectly
    const packingEfficiency = isPlotType ? 0.7 : 0.8; // strips pack more efficiently
    const estimatedPlots = Math.floor(totalPlotsInField * packingEfficiency);

    return estimatedPlots;
  }

  /**
   * Calculate field area from GeoJSON boundary
   * @param fieldBoundaryGeoJSON - GeoJSON FeatureCollection of the field boundary
   * @returns Field area in acres
   */
  public static calculateFieldAreaAcres(fieldBoundaryGeoJSON: any): number {
    if (!fieldBoundaryGeoJSON?.features?.[0]) {
      return 0;
    }

    try {
      const fieldBoundary = fieldBoundaryGeoJSON.features[0];
      const fieldAreaSqM = turf.area(fieldBoundary);

      // Convert to acres (1 square meter = 10.764 square feet)
      const fieldAreaSqFt = fieldAreaSqM * 10.764;
      const fieldAreaAcres =
        fieldAreaSqFt / PlotSizingCalculator.SQUARE_FEET_PER_ACRE;

      return fieldAreaAcres;
    } catch (error) {
      return 0;
    }
  }
}
